import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import BarcodeScanner from "../components/BarcodeScanner";
import { toast } from "sonner";
import { ScanLine, Trash2, Plus, Search } from "lucide-react";

/**
 * Generic mobile scanner workflow used by Wareneingang, Warenausgang, Umlagerung.
 *
 * Neben dem Barcode-Workflow (unverändert) unterstützt der Flow ab Phase B
 * eine Produktsuche über Name / EAN / SKU / Marke / Kategorie /
 * Lieferantenname (via ``GET /api/products?q=…`` — nutzt bewusst dieselbe
 * Repository-Search wie Produktverwaltung, damit kein zweiter Suchpfad
 * gepflegt werden muss).
 *
 * Props:
 *  - title, color, endpoint, transfer, showPrice, testIdPrefix
 */
export default function StockMovementFlow({ title, color, endpoint, transfer, showPrice, testIdPrefix }) {
  const [locationId, setLocationId] = useState("");
  const [toLocationId, setToLocationId] = useState("");
  const [lines, setLines] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [qty, setQty] = useState("1");
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  // Operations Polish 2026-08-30: Pflicht-„Grund" für WA (Whitelist).
  const [reason, setReason] = useState("");
  const [manualBarcode, setManualBarcode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Produktsuche-Popover (Phase B)
  const [searchQ, setSearchQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [debouncedQ, setDebouncedQ] = useState("");
  const searchWrapRef = useRef(null);
  const qc = useQueryClient();

  // Manueller Warenausgang verlangt einen strukturierten Pflicht-Grund.
  const requireReason = endpoint === "/goods-issues";
  const showSourceStock = endpoint === "/goods-issues" || endpoint === "/transfers";

  const locations = useQuery({
    queryKey: ["locations"],
    queryFn: async () => (await api.get("/locations", { params: { active: true } })).data,
  });

  // Polish-Package: Bestand am Quell-Lagerort für das gescannte Produkt.
  // Nur bei WA/UL geladen — kein API-Call ohne Location und ohne
  // ausgewähltes Produkt (`pending`).
  const sourceStock = useQuery({
    queryKey: ["source-stock", locationId, pending?.id],
    queryFn: async () => {
      const r = await api.get("/inventory", {
        params: { location_id: locationId, product_id: pending.id },
      });
      const row = (r.data || [])[0];
      return { quantity: row ? row.quantity : 0 };
    },
    enabled: !!(showSourceStock && locationId && pending?.id),
    staleTime: 0,
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchQ.trim()), 200);
    return () => clearTimeout(t);
  }, [searchQ]);

  const productSearch = useQuery({
    queryKey: ["product-search", debouncedQ],
    queryFn: async () =>
      (await api.get("/products", { params: { q: debouncedQ, active: true } })).data,
    enabled: debouncedQ.length >= 2,
  });

  // Popover schließen bei Klick außerhalb.
  useEffect(() => {
    function onDoc(e) {
      if (!searchWrapRef.current) return;
      if (!searchWrapRef.current.contains(e.target)) setSearchOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const colorMap = {
    green: "bg-emerald-500 hover:bg-emerald-400 text-zinc-950",
    red: "bg-rose-500 hover:bg-rose-400 text-white",
    violet: "bg-violet-500 hover:bg-violet-400 text-white",
  };
  const buttonColor = colorMap[color];

  const openQtyForProduct = useCallback((product) => {
    setPending(product);
    setQty("1");
    setPrice(product.purchase_price ? String(product.purchase_price) : "");
  }, []);

  const findAndAdd = useCallback(async (code) => {
    try {
      const { data: product } = await api.get("/products/lookup", { params: { ean: code } });
      openQtyForProduct(product);
      return { ok: true, name: product.name, ean: product.ean || code };
    } catch (e) {
      const msg = `Kein Produkt für „${code}" gefunden`;
      toast.error(msg);
      return { ok: false, message: msg };
    }
  }, [openQtyForProduct]);

  const pickSearchResult = (product) => {
    setSearchOpen(false);
    setSearchQ("");
    openQtyForProduct(product);
  };

  const onDetected = useCallback(
    async (code) => {
      if (pending) return { ok: false, message: "Menge zuerst bestätigen" };
      return await findAndAdd(code);
    },
    [pending, findAndAdd]
  );

  const commitLine = () => {
    if (!pending) return;
    const n = parseInt(qty, 10);
    if (!n || n <= 0) { toast.error("Menge muss > 0 sein"); return; }
    setLines((prev) => {
      const existingIdx = prev.findIndex((l) => l.product.id === pending.id);
      if (existingIdx >= 0) {
        const copy = [...prev];
        copy[existingIdx] = { ...copy[existingIdx], quantity: copy[existingIdx].quantity + n };
        return copy;
      }
      return [...prev, {
        product: pending, quantity: n,
        unit_price: showPrice && price ? parseFloat(price) : undefined,
      }];
    });
    setPending(null); setQty("1"); setPrice("");
  };

  const removeLine = (id) => setLines((prev) => prev.filter((l) => l.product.id !== id));

  const submit = async () => {
    if (!locationId) { toast.error("Lagerort wählen"); return; }
    if (transfer && !toLocationId) { toast.error("Ziel-Lagerort wählen"); return; }
    if (!lines.length) { toast.error("Keine Positionen erfasst"); return; }
    if (requireReason && !reason) {
      toast.error("Bitte einen Grund für den Warenausgang wählen.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = transfer
        ? {
            from_location_id: locationId, to_location_id: toLocationId,
            lines: lines.map((l) => ({ product_id: l.product.id, quantity: l.quantity })),
            notes: notes || undefined,
          }
        : endpoint === "/goods-issues"
        ? {
            location_id: locationId,
            lines: lines.map((l) => ({
              product_id: l.product.id, quantity: l.quantity,
              unit_price: l.unit_price,
            })),
            reason,
            notes: notes || undefined,
          }
        : {
            location_id: locationId,
            lines: lines.map((l) => ({
              product_id: l.product.id, quantity: l.quantity,
              unit_price: l.unit_price,
            })),
            notes: notes || undefined,
          };
      const { data } = await api.post(endpoint, payload);
      toast.success(`${title} erfolgreich gebucht`);
      setLines([]); setNotes(""); setReason("");
      qc.invalidateQueries({ queryKey: ["kpis"] });
      qc.invalidateQueries({ queryKey: ["recent"] });
      qc.invalidateQueries({ queryKey: ["inv-by-product"] });
      qc.invalidateQueries({ queryKey: ["txs"] });
      // ``data`` enthält jetzt weiterhin operation_id — genutzt in der
      // Buchungshistorie für den optionalen Beleg-Abruf. Nach Owner-
      // Freigabe (29.08.2026) wird bewusst KEIN Beleg mehr automatisch
      // erzeugt oder gedruckt — der Beleg entsteht ausschließlich manuell
      // aus der Buchungshistorie.
      void data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSubmitting(false); }
  };

  const totalUnits = useMemo(() => lines.reduce((s, l) => s + l.quantity, 0), [lines]);
  const usableLocations = (locations.data || []).filter((l) => l.id !== locationId);
  const results = productSearch.data || [];

  return (
    <div data-testid={`${testIdPrefix}-page`} className="pb-32">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-cyan-400">Buchung</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight">{title}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-400">
            {transfer ? "Quell-Lagerort" : "Lagerort"}
          </Label>
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger data-testid={`${testIdPrefix}-location-select`} className="mt-1 h-12 bg-zinc-900 border-zinc-800 rounded-sm">
              <SelectValue placeholder="Auswählen…" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              {(locations.data || []).map((l) => (
                <SelectItem key={l.id} value={l.id}>{l.name} <span className="text-zinc-500 font-mono ml-2">{l.code}</span></SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {transfer && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Ziel-Lagerort</Label>
            <Select value={toLocationId} onValueChange={setToLocationId}>
              <SelectTrigger data-testid={`${testIdPrefix}-to-location-select`} className="mt-1 h-12 bg-zinc-900 border-zinc-800 rounded-sm">
                <SelectValue placeholder="Auswählen…" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                {usableLocations.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name} <span className="text-zinc-500 font-mono ml-2">{l.code}</span></SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-4">
        <Input
          data-testid={`${testIdPrefix}-manual-barcode`}
          placeholder="EAN manuell eingeben"
          value={manualBarcode}
          onChange={(e) => setManualBarcode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && manualBarcode.trim()) {
              findAndAdd(manualBarcode.trim());
              setManualBarcode("");
            }
          }}
          className="h-14 bg-zinc-900 border-zinc-800 rounded-sm font-mono"
        />
        <Button
          data-testid={`${testIdPrefix}-scan-btn`}
          onClick={() => setScanOpen(true)}
          disabled={!locationId || (transfer && !toLocationId)}
          className={`h-14 rounded-sm uppercase tracking-wider font-semibold ${buttonColor} active:scale-[0.98] transition-transform`}
        >
          <ScanLine className="mr-2" size={18} /> Scan starten
        </Button>
      </div>

      {/* Produktsuche (Phase B) — zusätzliche, nicht ersetzende Auswahlmethode */}
      <div className="relative mb-4" ref={searchWrapRef}>
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
        <Input
          data-testid={`${testIdPrefix}-product-search`}
          placeholder="Produkt suchen: Name, EAN, SKU, Lieferant…"
          value={searchQ}
          onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
          onFocus={() => setSearchOpen(true)}
          disabled={!locationId || (transfer && !toLocationId)}
          className="pl-9 h-12 bg-zinc-900 border-zinc-800 rounded-sm"
        />
        {searchOpen && debouncedQ.length >= 2 && (
          <div
            data-testid={`${testIdPrefix}-product-search-results`}
            className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto rounded-sm border border-zinc-800 bg-zinc-950 shadow-lg"
          >
            {productSearch.isLoading && (
              <div className="px-3 py-3 text-sm text-zinc-500">Suche…</div>
            )}
            {!productSearch.isLoading && results.length === 0 && (
              <div className="px-3 py-3 text-sm text-zinc-500">Keine Treffer</div>
            )}
            {results.slice(0, 25).map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`${testIdPrefix}-product-search-item-${p.id}`}
                onClick={() => pickSearchResult(p)}
                className="w-full text-left px-3 py-2 hover:bg-zinc-900 border-b border-zinc-900 last:border-b-0 flex flex-col"
              >
                <span className="text-sm text-zinc-100 truncate">{p.name}</span>
                <span className="text-[11px] text-zinc-500 font-mono truncate">
                  {[p.ean, p.internal_id, p.brand, p.category].filter(Boolean).join(" · ") || "—"}
                </span>
              </button>
            ))}
            {results.length > 25 && (
              <div className="px-3 py-2 text-[11px] text-zinc-600">
                Nur die ersten 25 Treffer werden angezeigt — bitte konkreter suchen.
              </div>
            )}
          </div>
        )}
      </div>

      <div className="border border-zinc-800 bg-zinc-900 rounded-sm">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="font-display uppercase tracking-wide text-sm">Positionen</div>
          <div className="text-xs text-zinc-500 font-mono">
            {lines.length} Zeilen · {totalUnits} Einheiten
          </div>
        </div>
        <div className="divide-y divide-zinc-800" data-testid={`${testIdPrefix}-lines`}>
          {lines.length === 0 && (
            <div className="p-6 text-sm text-zinc-500 text-center">
              Noch keine Positionen. Barcode scannen, EAN eingeben oder Produkt suchen.
            </div>
          )}
          {lines.map((l) => (
            <div key={l.product.id} className="px-4 py-3 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="truncate">{l.product.name}</div>
                <div className="text-xs text-zinc-500 font-mono">{l.product.ean || l.product.internal_id || "—"}</div>
              </div>
              <div className="font-mono tabular-nums text-xl">×{l.quantity}</div>
              <Button
                data-testid={`line-remove-${l.product.id}`}
                size="icon" variant="ghost" className="text-zinc-500 hover:text-rose-400"
                onClick={() => removeLine(l.product.id)}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {requireReason && (
          <div data-testid="wa-reason-wrap">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              Grund *
            </Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger
                data-testid="wa-reason-select"
                className="mt-1 h-11 bg-zinc-900 border-zinc-800 rounded-sm text-foreground"
              >
                <SelectValue placeholder="Grund wählen…" />
              </SelectTrigger>
              <SelectContent className="bg-popover text-popover-foreground border-zinc-800">
                <SelectItem value="eigengebrauch">Eigengebrauch</SelectItem>
                <SelectItem value="defekt">Defekt</SelectItem>
                <SelectItem value="verlust">Verlust</SelectItem>
                <SelectItem value="retoure">Retoure / Rückgabe</SelectItem>
                <SelectItem value="sonstiges">Sonstiges</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-zinc-500 mt-1">
              Pflichtfeld — strukturierter Grund für Filter/Export in der Buchungshistorie.
            </div>
          </div>
        )}
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-400">
            Bemerkung (optional)
          </Label>
          <Textarea
            data-testid={`${testIdPrefix}-notes`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1 bg-zinc-900 border-zinc-800 rounded-sm"
            rows={2}
            placeholder={requireReason ? "Zusätzliche Details (optional)…" : ""}
          />
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-zinc-950/95 border-t border-zinc-800 p-4 z-20"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
        <Button
          data-testid={`${testIdPrefix}-submit-btn`}
          onClick={submit}
          disabled={submitting || !lines.length || (requireReason && !reason)}
          className={`w-full h-16 rounded-sm uppercase tracking-wider font-bold text-base ${buttonColor} active:scale-[0.99] transition-transform`}
        >
          {submitting ? "Buche…" : `${title} buchen · ${totalUnits} Einheiten`}
        </Button>
      </div>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={onDetected} />

      {pending && (
        <div className="fixed inset-0 z-[60] flex items-end lg:items-center justify-center bg-black/70 p-4" data-testid="qty-prompt">
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm w-full max-w-md p-5">
            <div className="text-xs uppercase tracking-widest text-cyan-400 mb-1">Produkt erkannt</div>
            <div className="font-display text-2xl font-bold uppercase tracking-tight">{pending.name}</div>
            <div className="text-sm text-zinc-500 font-mono mb-4">{pending.ean || pending.internal_id}</div>
            {showSourceStock && (
              <div
                data-testid="source-stock-badge"
                className="mb-4 rounded-sm border border-zinc-800 bg-zinc-900 px-3 py-2 flex items-center justify-between"
              >
                <div className="text-[11px] uppercase tracking-widest text-zinc-500">
                  Bestand am Quell-Lagerort
                </div>
                <div
                  data-testid="source-stock-value"
                  className={`font-mono tabular-nums text-xl font-black ${
                    sourceStock.isLoading
                      ? "text-zinc-500"
                      : (sourceStock.data?.quantity || 0) <= 0
                      ? "text-rose-400"
                      : "text-cyan-300"
                  }`}
                >
                  {sourceStock.isLoading ? "…" : (sourceStock.data?.quantity ?? 0)}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-400">Menge</Label>
                <Input
                  data-testid="qty-input"
                  type="number" inputMode="numeric" min={1}
                  value={qty} onChange={(e) => setQty(e.target.value)}
                  className="mt-1 h-14 bg-zinc-900 border-zinc-800 rounded-sm text-2xl font-mono"
                  autoFocus
                />
              </div>
              {showPrice && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-400">EK (€)</Label>
                  <Input
                    data-testid="price-input"
                    type="number" step="0.01"
                    value={price} onChange={(e) => setPrice(e.target.value)}
                    className="mt-1 h-14 bg-zinc-900 border-zinc-800 rounded-sm text-2xl font-mono"
                  />
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-4">
              <Button variant="ghost" className="flex-1" onClick={() => setPending(null)}>Abbrechen</Button>
              <Button
                data-testid="qty-confirm-btn"
                onClick={commitLine}
                className={`flex-1 h-12 rounded-sm ${buttonColor}`}
              >
                <Plus className="mr-2" size={16} /> Übernehmen
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
